import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Download, RefreshCw, Image as ImageIcon, User } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`; // kept for external asset URLs if needed

const AdminMiniatures = () => {
    const [jobs, setJobs] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [j, p] = await Promise.all([
                api.get("/miniature/admin/jobs"),
                api.get("/miniature/admin/payments"),
            ]);
            setJobs(j.data.jobs || []);
            setPayments(p.data.payments || []);
        } catch (e) {
            toast.error("Could not load miniature data");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const verify = async (id) => {
        try {
            await api.post(`/miniature/admin/payments/${id}/verify`);
            toast.success("Payment verified — customer can now continue");
            load();
        } catch (e) { toast.error("Could not verify"); }
    };
    const reject = async (id) => {
        try {
            await api.post(`/miniature/admin/payments/${id}/reject`);
            toast.success("Payment rejected");
            load();
        } catch (e) { toast.error("Could not reject"); }
    };

    // Admin file endpoints need the Bearer token — plain <a href> gets 401. Fetch with auth, then open/download.
    const openAdminFile = async (endpoint, filename, download = false) => {
        try {
            const res = await api.get(endpoint, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            if (download) {
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                a.click();
            } else {
                window.open(url, "_blank", "noopener,noreferrer");
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            toast.error("File not available for this job");
        }
    };

    const badge = (s) => ({
        pending: "bg-amber-100 text-amber-800",
        running: "bg-amber-100 text-amber-800",
        success: "bg-emerald-100 text-emerald-800",
        error: "bg-red-100 text-red-800",
        awaiting_utr: "bg-slate-100 text-slate-700",
        utr_submitted: "bg-amber-100 text-amber-800",
        verified: "bg-emerald-100 text-emerald-800",
        rejected: "bg-red-100 text-red-800",
    }[s] || "bg-slate-100 text-slate-700");

    return (
        <div className="space-y-6" data-testid="admin-miniatures-page">
            <div className="flex justify-between items-center">
                <h1 className="font-display text-3xl font-bold">Custom Miniatures</h1>
                <Button onClick={load} variant="outline" className="rounded-full border-primary/40 text-primary"><RefreshCw className="w-4 h-4" /> Refresh</Button>
            </div>

            <section className="rounded-3xl glass p-6">
                <h2 className="font-display text-xl font-semibold mb-3">QR Payments awaiting verification</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left border-b border-border">
                            <tr><th className="py-2">Purpose</th><th>Amount</th><th>UTR</th><th>Job</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                            {payments.filter(p => p.status === "utr_submitted").map((p) => (
                                <tr key={p.id} className="border-b border-border/40" data-testid={`pay-row-${p.id}`}>
                                    <td className="py-2 capitalize">{p.purpose.replace("_", " ")}</td>
                                    <td>₹{p.amount_inr}</td>
                                    <td className="font-mono text-xs">{p.utr}</td>
                                    <td className="font-mono text-xs">{p.job_id?.slice(0, 8)}</td>
                                    <td><span className={`px-2 py-0.5 rounded-full text-xs ${badge(p.status)}`}>{p.status}</span></td>
                                    <td className="text-right space-x-2">
                                        <Button size="sm" onClick={() => verify(p.id)} data-testid={`verify-${p.id}`} className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-3 h-3" /> Verify</Button>
                                        <Button size="sm" variant="outline" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`} className="rounded-full h-8 border-red-300 text-red-600"><XCircle className="w-3 h-3" /> Reject</Button>
                                    </td>
                                </tr>
                            ))}
                            {payments.filter(p => p.status === "utr_submitted").length === 0 && (
                                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground text-sm">No pending payments to verify.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="rounded-3xl glass p-6">
                <h2 className="font-display text-xl font-semibold mb-3">All Generations</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left border-b border-border">
                            <tr><th className="py-2">Job</th><th>Gen #</th><th>Cost</th><th>Status</th><th>STL</th><th>Photo</th><th>Approved</th><th>Created</th></tr>
                        </thead>
                        <tbody>
                            {jobs.map((j) => (
                                <tr key={j.id} className="border-b border-border/40" data-testid={`job-row-${j.id}`}>
                                    <td className="py-2 font-mono text-xs">{j.id.slice(0, 8)}</td>
                                    <td>#{j.generation_number}</td>
                                    <td>{j.cost_inr === 0 ? "FREE" : `₹${j.cost_inr}`}</td>
                                    <td><span className={`px-2 py-0.5 rounded-full text-xs ${badge(j.status)}`}>{j.status}</span></td>
                                    <td>
                                        {j.status === "success" ? (
                                            <button onClick={() => openAdminFile(`/miniature/admin/job/${j.id}/stl`, `miniature-${j.id.slice(0, 8)}.stl`, true)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`dl-stl-${j.id}`}>
                                                <Download className="w-3 h-3" /> STL
                                            </button>
                                        ) : "—"}
                                    </td>
                                    <td>
                                        {j.photo_path ? (
                                            <button onClick={() => openAdminFile(`/miniature/admin/job/${j.id}/photo`, `photo-${j.id.slice(0, 8)}.jpg`)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`view-photo-${j.id}`}>
                                                <ImageIcon className="w-3 h-3" /> View
                                            </button>
                                        ) : "—"}
                                    </td>
                                    <td>{j.approved ? <span className="text-emerald-600">✓</span> : "—"}</td>
                                    <td className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                            {jobs.length === 0 && (
                                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground text-sm">No custom miniature jobs yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AdminMiniatures;
